import { PCI_TRACKING_NAME } from '@/features/purchase/components/PciSections/constants'
import { setItem, removeItem, getItem } from '@/features/shared/utils/localStorage'
import { logWarn } from '@/features/shared/utils/logger'
import { getIsCurrentRoutePCI } from '@shared/constants/routes'
import { type PageEventProperties } from '@shipt/analytics-core'
import { gtmDestinationEvents, type AllAnalyticsEvents } from '@shipt/analytics-member-web'

export const pushEventToDataLayer = ({ eventId, eventName, properties }: DataLayerEvent) => {
  // https://support.google.com/tagmanager/answer/7679219?hl=en&ref_topic=7679108
  if (shouldSendToGoogle(eventName)) {
    // this logic to ensure the window.dataLayer is initialized before window.dataLayer.push
    // is invoked since the GTM script is potentially loaded due to the transcend script being
    // a blocking script and the GTM script being async.
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({ event: eventName, ...properties, eventId })
  }
}

export const shouldSendToGoogle = (eventName: string) =>
  Boolean(process.env.NEXT_PUBLIC_GTM_CONTAINER_ID && eventName in gtmDestinationEvents)

export const flushPciTrackingQueue = () => {
  if (getIsCurrentRoutePCI()) return

  const storageEventQueue = getStoredEvents()
  try {
    while (storageEventQueue.length) {
      const nextEvent = storageEventQueue.shift()
      if (nextEvent) {
        pushEventToDataLayer(nextEvent)
      }
      // for each loop, save the state of the queue after sending each event
      // this is to prevent potential lost events if an error were to happen while processing events
      setItem(PCI_TRACKING_NAME, JSON.stringify(storageEventQueue))
    }
    removeItem(PCI_TRACKING_NAME)
  } catch {
    logWarn('Failed to send all PCI queued events. Will try again upon refresh', {
      tag: 'pci',
    })
  }
}

type DataLayerPageEvent = {
  eventName: 'Loaded a Page'
  properties: Partial<PageEventProperties> & {
    userId?: string | null
    segmentAnonymousId: string
  }
  eventId: string
}
type DataLayerTrackEvent = AllAnalyticsEvents & { eventId: string }

export type DataLayerEvent = DataLayerTrackEvent | DataLayerPageEvent

const getStoredEvents = (eventToAdd?: string): DataLayerEvent[] => {
  let storageEventQueue: DataLayerEvent[] = []
  try {
    const pciQueue = getItem(PCI_TRACKING_NAME)
    const parsedQueue = pciQueue ? JSON.parse(pciQueue) : []
    if (!Array.isArray(parsedQueue)) {
      logWarn('Found non array PCI queue. Resetting storage', {
        tag: 'pci',
      })
      removeItem(PCI_TRACKING_NAME)
    } else {
      storageEventQueue = parsedQueue
    }
  } catch {
    logWarn(
      'Failed to parse pciQueue JSON from localStorage. Resetting queue',
      eventToAdd ? { eventName: eventToAdd, tag: 'pci' } : { tag: 'pci' },
    )
    removeItem(PCI_TRACKING_NAME)
  }
  return storageEventQueue
}

// third party scripts (like GTM) are not loaded on PCI pages for security reasons.
// this utility stores eligible analytics events in local storage so they can be queued safely.
// once the user navigates to a non PCI page, the queued events are flushed and sent to Google.
export const queueOrPushEvent = (event: DataLayerEvent) => {
  // if it is not a PCI page, then the event is pushed directly to the data layer for GTM.
  if (!getIsCurrentRoutePCI()) return pushEventToDataLayer(event)

  // if the event should not be sent to Google, do not queue it
  if (!shouldSendToGoogle(event.eventName)) return

  const storageEventQueue = getStoredEvents(event.eventName)

  try {
    setItem(PCI_TRACKING_NAME, JSON.stringify([event, ...storageEventQueue]))
  } catch {
    logWarn('Failed to send all PCI queued events. Will try again upon refresh', { tag: 'pci' })
  }
}
